import React, { useState } from "react";
import { Mail, KeyRound, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AccountSettings({ currentEmail, onEmailChanged }) {
  return (
    <div className="bg-white rounded-2xl border border-border cornell-shadow p-5 space-y-6" data-testid="account-settings">
      <div>
        <h2 className="text-lg font-extrabold">Account settings</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Update your email or change your password.</p>
      </div>
      <ChangeEmailForm currentEmail={currentEmail} onChanged={onEmailChanged} />
      <div className="border-t border-border" />
      <ChangePasswordForm />
    </div>
  );
}

function ChangeEmailForm({ currentEmail, onChanged }) {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (trimmed === (currentEmail || "").toLowerCase()) {
      setError("New email is the same as your current email.");
      return;
    }
    if (!currentPassword) {
      setError("Enter your current password to confirm this change.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-email", {
        new_email: trimmed,
        current_password: currentPassword,
      });
      if (data?.token) localStorage.setItem("ccneo_token", data.token);
      onChanged && onChanged(data.user);
      setNewEmail("");
      setCurrentPassword("");
      toast.success("Email address updated");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-email-form">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#B31B1B]/10 text-[#B31B1B] grid place-items-center">
          <Mail className="w-4 h-4" />
        </div>
        <div>
          <div className="font-semibold text-sm">Email address</div>
          <div className="text-xs text-muted-foreground" data-testid="current-email-display">
            Current: {currentEmail}
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor="new-email">New email</Label>
        <Input
          id="new-email"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="you@cornell.edu"
          data-testid="new-email-input"
          required
        />
      </div>
      <div>
        <Label htmlFor="email-current-password">Current password (to confirm)</Label>
        <Input
          id="email-current-password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          data-testid="email-current-password-input"
          required
        />
      </div>
      {error && (
        <div className="text-sm text-[#B31B1B] bg-[#B31B1B]/5 border border-[#B31B1B]/20 px-3 py-2 rounded-md" data-testid="change-email-error">
          {error}
        </div>
      )}
      <Button
        type="submit"
        disabled={submitting}
        className="bg-[#B31B1B] hover:bg-[#8f1616] gap-2"
        data-testid="change-email-submit-btn"
      >
        <Save className="w-4 h-4" /> {submitting ? "Saving…" : "Update email"}
      </Button>
    </form>
  );
}

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!currentPassword) { setError("Enter your current password."); return; }
    if (newPassword.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (newPassword !== confirmPassword) { setError("New password and confirmation do not match."); return; }
    if (newPassword === currentPassword) { setError("New password must be different from current password."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (data?.token) localStorage.setItem("ccneo_token", data.token);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast.success("Password updated");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#B31B1B]/10 text-[#B31B1B] grid place-items-center">
          <KeyRound className="w-4 h-4" />
        </div>
        <div>
          <div className="font-semibold text-sm">Password</div>
          <div className="text-xs text-muted-foreground">Use at least 6 characters.</div>
        </div>
      </div>
      <div>
        <Label htmlFor="pw-current">Current password</Label>
        <Input
          id="pw-current" type="password"
          value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
          data-testid="pw-current-input" required
        />
      </div>
      <div>
        <Label htmlFor="pw-new">New password</Label>
        <Input
          id="pw-new" type="password" minLength={6}
          value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
          data-testid="pw-new-input" required
        />
      </div>
      <div>
        <Label htmlFor="pw-confirm">Confirm new password</Label>
        <Input
          id="pw-confirm" type="password" minLength={6}
          value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
          data-testid="pw-confirm-input" required
        />
      </div>
      {error && (
        <div className="text-sm text-[#B31B1B] bg-[#B31B1B]/5 border border-[#B31B1B]/20 px-3 py-2 rounded-md" data-testid="change-password-error">
          {error}
        </div>
      )}
      <Button
        type="submit"
        disabled={submitting}
        className="bg-[#B31B1B] hover:bg-[#8f1616] gap-2"
        data-testid="change-password-submit-btn"
      >
        <Save className="w-4 h-4" /> {submitting ? "Saving…" : "Update password"}
      </Button>
    </form>
  );
}
